/**
 * Hardcoded planned/backlog source definitions that are not yet implemented
 * on the server. Each definition carries an i18n key for the description so
 * the React layer can resolve the translated string via `t(def.descriptionKey)`.
 *
 * At runtime these are merged with API-returned sources and any whose `id`
 * already exists in the API response is silently dropped (i.e. when a source
 * graduates from planned → live, it automatically disappears from the backlog
 * section without any code change here).
 */

export interface PlannedSourceDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly category: string;
  /** i18n key resolved by the presentation layer – e.g. `board.connections.sources.planned.aha.description` */
  readonly descriptionKey: string;
  readonly order: number;
}

export const HARDCODED_PLANNED_SOURCE_DEFINITIONS: readonly PlannedSourceDefinition[] =
  [
    // ── Project Management ──────────────────────────────────────────────────────
    {
      id: "aha",
      displayName: "Aha!",
      category: "Project Management",
      descriptionKey: "board.connections.sources.planned.aha.description",
      order: 900,
    },
    {
      id: "wrike",
      displayName: "Wrike",
      category: "Project Management",
      descriptionKey: "board.connections.sources.planned.wrike.description",
      order: 901,
    },
    // ── Development ─────────────────────────────────────────────────────────────
    {
      id: "bugzilla",
      displayName: "Bugzilla",
      category: "Development",
      descriptionKey: "board.connections.sources.planned.bugzilla.description",
      order: 902,
    },
    {
      id: "bitbucket",
      displayName: "BitBucket",
      category: "Development",
      descriptionKey: "board.connections.sources.planned.bitbucket.description",
      order: 903,
    },
    // ── Tasks ───────────────────────────────────────────────────────────────────
    {
      id: "nozbe",
      displayName: "Nozbe",
      category: "Tasks",
      descriptionKey: "board.connections.sources.planned.nozbe.description",
      order: 904,
    },
    {
      id: "remember-the-milk",
      displayName: "Remember the Milk",
      category: "Tasks",
      descriptionKey:
        "board.connections.sources.planned.remember-the-milk.description",
      order: 905,
    },
    {
      id: "toodledo",
      displayName: "Toodledo",
      category: "Tasks",
      descriptionKey: "board.connections.sources.planned.toodledo.description",
      order: 906,
    },
    {
      id: "googleclassroom",
      displayName: "Google Classroom",
      category: "Tasks",
      descriptionKey:
        "board.connections.sources.planned.googleclassroom.description",
      order: 908,
    },
    // ── Notes & Files ───────────────────────────────────────────────────────────
    {
      id: "evernote",
      displayName: "Evernote",
      category: "Notes & Files",
      descriptionKey: "board.connections.sources.planned.evernote.description",
      order: 907,
    },
    {
      id: "excel-online",
      displayName: "Microsoft Excel Online",
      category: "Notes & Files",
      descriptionKey:
        "board.connections.sources.planned.excel-online.description",
      order: 910,
    },
    // ── Support & IT ────────────────────────────────────────────────────────────
    {
      id: "haloitsm",
      displayName: "HaloITSM",
      category: "Support & IT",
      descriptionKey: "board.connections.sources.planned.haloitsm.description",
      order: 911,
    },
    {
      id: "servicenow",
      displayName: "ServiceNow",
      category: "Support & IT",
      descriptionKey:
        "board.connections.sources.planned.servicenow.description",
      order: 912,
    },
    // ── Business ────────────────────────────────────────────────────────────────
    {
      id: "concur",
      displayName: "Concur",
      category: "Business",
      descriptionKey: "board.connections.sources.planned.concur.description",
      order: 913,
    },
    {
      id: "greenhouse",
      displayName: "Greenhouse",
      category: "Business",
      descriptionKey:
        "board.connections.sources.planned.greenhouse.description",
      order: 914,
    },
    {
      id: "uservoice",
      displayName: "Uservoice",
      category: "Business",
      descriptionKey: "board.connections.sources.planned.uservoice.description",
      order: 915,
    },
    // ── Integration ─────────────────────────────────────────────────────────────
    {
      id: "odata",
      displayName: "OData Feed",
      category: "Integration",
      descriptionKey: "board.connections.sources.planned.odata.description",
      order: 916,
    },
    {
      id: "rss",
      displayName: "RSS Feed",
      category: "Integration",
      descriptionKey: "board.connections.sources.planned.rss.description",
      order: 917,
    },
  ];
